import { EditableText, EditableTitle } from "@/components/EditableText";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { ExpertDisplayName } from "@/components/display/ExpertDisplayName";
import { UserDisplayName } from "@/components/display/UserDisplayName";
import { Markdown } from "@/components/markdown/Markdown";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useChatsData } from "@/data/use-chats-data";
import { useExpertsData } from "@/data/use-experts-data";
import { useMessagesData } from "@/data/use-messages-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { smartDate } from "@/lib/date";
import { GetChatsResponseDto } from "@/lib/gen-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import { useEffect, useState } from "react";
import { useKitData } from "../../data/use-kits-data";

export const InterviewAdminPage = () => {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();

  const { kit, updateKit } = useKitData(groupUuid, kitUuid);
  const { chats } = useChatsData(groupUuid, kitUuid, {
    allUsers: true,
  });
  const [selectedChatUuid, setSelectedChatUuid] = useState<string | null>(null);
  const { messages } = useMessagesData(groupUuid, kitUuid, selectedChatUuid);
  const { experts } = useExpertsData(groupUuid, kitUuid);

  useEffect(() => {
    if (chats.length > 0 && !selectedChatUuid) {
      setSelectedChatUuid(chats[0].uuid);
    }
  }, [chats]);

  const selectedChat = chats.find((chat) => chat.uuid === selectedChatUuid);

  const chatTitle = (chat: GetChatsResponseDto["data"][number]) => {
    return (
      <div className="flex items-center gap-4">
        {chat.user ? <UserDisplayName user={chat.user} /> : null}
        <div>({smartDate(chat.dateCreated)})</div>
      </div>
    );
  };

  const handleNameUpdate = async (newName: string) => {
    if (!kit) return;
    await updateKit({ name: newName });
  };

  const handleDescriptionUpdate = async (newDescription: string) => {
    if (!kit) return;
    await updateKit({ description: newDescription });
  };

  if (!kit) {
    return null;
  }

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>
          <EditableTitle value={kit.name} onUpdate={handleNameUpdate} />
        </PageHeader.Title>
      </PageHeader>
      <PageBody>
        <PageBody.Metadata>
          <PageBody.MetadataGroup>
            <EditableText
              value={kit.description ?? ""}
              onUpdate={handleDescriptionUpdate}
              placeholder="Description..."
            />
          </PageBody.MetadataGroup>
        </PageBody.Metadata>
        <div className="flex flex-col gap-4">
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  {selectedChat && chatTitle(selectedChat)}{" "}
                  <FontAwesomeIcon icon={faCaretDown} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {chats.map((chat) => (
                  <DropdownMenuItem
                    key={chat.uuid}
                    onClick={() => setSelectedChatUuid(chat.uuid)}
                  >
                    {chatTitle(chat)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="text-lg font-bold">Messages:</div>
          <div className="mt-4 flex flex-col gap-8">
            {messages
              .filter(
                (message) =>
                  message.type === "user" || message.type === "model",
              )
              .map((message) => {
                const expert = experts.find(
                  (ex) => ex.uuid === message.chat.expertUuid,
                );
                const user = selectedChat?.user;
                return (
                  <div key={message.uuid} className="flex flex-col gap-4">
                    <div className="w-40 text-sm font-semibold">
                      {message.type === "user" ? (
                        user ? (
                          <UserDisplayName user={user} />
                        ) : (
                          "Unknown User"
                        )
                      ) : expert ? (
                        <ExpertDisplayName expert={expert} />
                      ) : (
                        "Unknown Expert"
                      )}
                    </div>
                    <div className="markdown flex-1">
                      <Markdown allowRawHtml>{message.content}</Markdown>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </PageBody>
    </Page>
  );
};
